import { useCallback, useState } from 'react';
import {
  getClientVersionGoIteens,
  getCurrentVersion,
  getDefaultOrganization,
  getWebConfig,
  updateCurrentVersion,
  updateDefaultOrganization
} from '../api/apiWorker';
import { message } from 'antd';
import { ChangeWebAppVersionsFormValues, IWebConfig, UpdateVersionsConfigId } from '../types';

export const useGetCurrentVersion = () => {
  const [loading, setLoading] = useState(false);
  const [loadingWebConfig, setLoadingWebConfig] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('');
  const [currentClientVersionGoIteens, setCurrentClientVersionGoIteens] = useState('');
  const [currentWebConfig, setCurrentWebConfig] = useState<IWebConfig | null>(null);
  const [currentOrganizationId, setCurrentOrganizationId] = useState('');

  const fetchCurrentVersion = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCurrentVersion();
      const resultGoIteens = await getClientVersionGoIteens();

      setCurrentVersion(result);
      setCurrentClientVersionGoIteens(resultGoIteens);
    } catch (e) {
      message.error('Unable to fetch version');
      console.error(e, '[fetchCurrentVersion]');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWebbAppVersions = useCallback(async () => {
    setLoadingWebConfig(true);
    try {
      const result = await getWebConfig();
      result && setCurrentWebConfig(result);
    } catch (e) {
      message.error('Unable to fetch web config');
      console.error(e, '[fetchWebbAppVersions]');
    } finally {
      setLoadingWebConfig(false);
    }
  }, []);

  const updateClientVersion = useCallback(async (configId: UpdateVersionsConfigId, newVersion: string) => {
    setLoading(true);
    try {
      await updateCurrentVersion({ configId: configId, newConfig: newVersion });

      configId === 'client_version' ? setCurrentVersion(newVersion) : setCurrentClientVersionGoIteens(newVersion);
      message.success('Version was successfully updated');
    } catch (error) {
      message.error('Unable to update version');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateWebAppConfig = useCallback(
    async ({
      configId,
      newConfig
    }: {
      configId: 'builds_download_urls' | 'goiteens_builds_download_urls';
      newConfig: ChangeWebAppVersionsFormValues;
    }) => {
      setLoading(true);
      try {
        await updateCurrentVersion({ configId: configId, newConfig: newConfig });
        setCurrentWebConfig(
          prev =>
            ({
              ...prev,
              [configId]: newConfig[configId]
            }) as IWebConfig
        );
        message.success('Version was successfully updated');
      } catch (error) {
        message.error('Unable to update version');
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchCurrentOrganization = useCallback(async () => {
    try {
      const result = await getDefaultOrganization();
      result && setCurrentOrganizationId(result);
    } catch (e) {
      // message.error('Unable to fetch current organization');
      console.error(e, '[fetchCurrentOrganization]');
    }
  }, []);

  const updateOrganization = useCallback(async (orgId: string) => {
    try {
      await updateDefaultOrganization(orgId);
      setCurrentOrganizationId(orgId);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    fetchCurrentVersion,
    updateClientVersion,
    currentVersion,
    loading,
    loadingWebConfig,
    currentWebConfig,
    currentClientVersionGoIteens,
    currentOrganizationId,
    fetchWebbAppVersions,
    updateWebAppConfig,
    fetchCurrentOrganization,
    updateDefaultOrganization,
    updateOrganization
  };
};
